import { useCallback, useMemo } from 'react';
import { ApolloError } from 'apollo-client';
import { useTheme } from 'styled-components';

import type { Product } from 'src/utils/helpers/product';
import { PersonalizedProductsQuery } from 'src/personalization/data-layer/types';
import useViewportVisibility, { ViewportVisibility } from 'src/hooks/use-viewport-visibility';

import { ItemListName } from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';

import { useCardDimensions } from 'src/components/product-card/product-card.utils';
import type { Theme } from './types';

import { usePersonalizedProducts } from './use-personalized-products';

type UsePersonalizedProductsCarouselParams = {
  maxProducts?: number;
  personalizedProductsQuery: PersonalizedProductsQuery;
};

type UsePersonalizedProductsCarouselReturn = {
  dimensions: ReturnType<typeof useCardDimensions>;
  error?: ApolloError;
  loading: boolean;
  products: Product[];
  theme: Theme;
  handleProductClick: (product: Product, index: number, trackerSourceOverride?: ItemListName) => void;
  handleQuickAddToCart: (product: Product, index: number, trackerSourceOverride?: ItemListName) => void;
  viewportVisibility: ViewportVisibility;
};

export const usePersonalizedProductsCarousel = ({
  maxProducts = 20,
  personalizedProductsQuery,
}: UsePersonalizedProductsCarouselParams): UsePersonalizedProductsCarouselReturn => {
  const viewportVisibility = useViewportVisibility();

  const {
    products: personalizedProducts,
    loading,
    error,
    handleProductClick: personalizedProductClick,
    handleQuickAddToCart: personalizedProductAddToCart,
  } = usePersonalizedProducts({
    personalizedProductsQuery,
  });

  const dimensions = useCardDimensions({ gutter: 12 });
  const { customized } = useTheme();
  const theme = {
    backgroundColor: '#f5f6f7',
    primaryColor: customized.colors.buttonsLinks,
  };

  const products = useMemo(() => {
    if (loading) {
      return [];
    }

    return personalizedProducts.slice(0, maxProducts);
  }, [loading, personalizedProducts, maxProducts]);

  const handleProductClick = useCallback(
    (product, index, trackerSourceOverride) => {
      void personalizedProductClick(product, index, trackerSourceOverride);
    },
    [personalizedProductClick]
  );

  const handleQuickAddToCart = useCallback(
    (product, index, trackerSourceOverride) => {
      void personalizedProductAddToCart(product, index, trackerSourceOverride);
    },
    [personalizedProductAddToCart]
  );

  return {
    products,
    loading,
    error,
    theme,
    dimensions,
    handleProductClick,
    handleQuickAddToCart,
    viewportVisibility,
  };
};
